import type { TeamNotification } from "@redotech/effect-api-shared/models/team/notification";
import { TeamNotificationJson } from "@redotech/effect-api-shared/models/team/notification";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import { memo, useContext, useMemo, type ReactNode } from "react";
import { createNotificationStreamProvider } from "./notification-stream-provider";

// Create typed provider and hook for TeamNotification schema
const {
  Provider: BaseProvider,
  useNotificationStream: useTeamNotificationStream,
} = createNotificationStreamProvider<TeamNotification>();

// Export typed hook for use throughout the app
export { useTeamNotificationStream };

/**
 * TeamNotificationStreamProvider
 *
 * Team-specific wrapper around the generic NotificationStreamProvider.
 * Handles team notification endpoint URL construction, authentication, and schema.
 *
 * This provider:
 * - Constructs the team notifications endpoint URL
 * - Passes auth token from AuthContext
 * - Provides the TeamNotification schema for validation
 * - Creates a typed provider/hook via the factory
 *
 * Use this in your app to establish the team notification stream.
 * Then use `useTeamNotificationStream` hook to subscribe to events.
 */
export const TeamNotificationStreamProvider = memo(
  function TeamNotificationStreamProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const auth = useContext(AuthContext);

    const streamUrl = useMemo(
      () =>
        new URL(
          "/api/streams/team/notifications",
          REDO_EFFECT_API_URL,
        ).toString(),
      [],
    );

    return (
      <BaseProvider
        schema={TeamNotificationJson}
        token={auth?.token}
        url={streamUrl}
      >
        {children}
      </BaseProvider>
    );
  },
);
